<template>
  <div class="categories-carousel-wrapper" @mouseenter="pauseAutoPlay" @mouseleave="resumeAutoPlay">
    <div class="categories-carousel" ref="carousel" :style="carouselStyle">
      <div 
        v-for="(category, index) in extendedCategories" 
        :key="`${category.id}-${index}`"
        class="carousel-item"
      >
        <a :href="getCategoryUrl(category)" class="featured-links__item image-card">
          <figure>
            <img
              :src="category.image_url || defaultImage"
              :alt="category.name"
              loading="lazy"
            />
            <figcaption>
              <h4 v-if="category.overline" class="image-card__overline">{{ category.overline }}</h4>
              <h3 class="h2 image-card__title">{{ category.name }}</h3>
              <p v-if="category.blurb">{{ truncateText(category.blurb, 250) }}</p>
            </figcaption>
          </figure>
        </a>
      </div>
    </div>
    
    <!-- Navigation Controls -->
    <div class="carousel-navigation">
      <button 
        class="carousel-nav carousel-prev" 
        @click="prevSlide"
        aria-label="Previous categories"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
      
      <!-- Dots Indicator -->
      <div class="carousel-dots">
        <button 
          v-for="(dot, index) in totalDots" 
          :key="index"
          class="carousel-dot"
          :class="{ 'active': index === currentDotIndex }"
          @click="goToSlide(index)"
          :aria-label="`Go to slide ${index + 1}`"
        ></button>
      </div>
      
      <button 
        class="carousel-nav carousel-next" 
        @click="nextSlide"
        aria-label="Next categories"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 18l6-6-6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'

const props = defineProps({
  categories: {
    type: Array,
    required: true
  },
  autoPlayInterval: {
    type: Number,
    default: 4000
  },
  animationDuration: {
    type: Number,
    default: 500
  },
  defaultImage: {
    type: String,
    default: '/images/default.jpg'
  }
})

const carousel = ref(null)
const currentIndex = ref(0)
const autoPlayTimer = ref(null)
const isPaused = ref(false)
const itemsPerView = ref(1)
const isTransitioning = ref(false)

// Create extended categories array for infinite loop
const extendedCategories = computed(() => {
  if (props.categories.length <= itemsPerView.value) {
    return props.categories
  }
  
  // Clone first few items to end and last few items to beginning for seamless loop
  const cloneCount = itemsPerView.value
  const start = props.categories.slice(-cloneCount)
  const end = props.categories.slice(0, cloneCount)
  
  return [...start, ...props.categories, ...end]
})

// Computed properties
const totalOriginalItems = computed(() => props.categories.length)
const totalDots = computed(() => Math.ceil(totalOriginalItems.value / itemsPerView.value))
const currentDotIndex = computed(() => {
  const adjustedIndex = currentIndex.value - itemsPerView.value
  return Math.floor(Math.max(0, adjustedIndex) / itemsPerView.value) % totalDots.value
})

const carouselStyle = computed(() => ({
  transform: `translateX(-${(currentIndex.value * 100) / itemsPerView.value}%)`,
  transition: isTransitioning.value ? `transform ${props.animationDuration}ms ease-in-out` : 'none'
}))

// Methods
const updateItemsPerView = () => {
  const width = window.innerWidth
  const oldItemsPerView = itemsPerView.value
  
  if (width >= 992) {
    itemsPerView.value = 3
  } else if (width >= 768) {
    itemsPerView.value = 2
  } else {
    itemsPerView.value = 1
  }
  
  // Reset to initial position if items per view changed
  if (oldItemsPerView !== itemsPerView.value) {
    resetToStart()
  }
}

const resetToStart = () => {
  if (props.categories.length > itemsPerView.value) {
    currentIndex.value = itemsPerView.value // Start at the first real item
  } else {
    currentIndex.value = 0
  }
}

const nextSlide = () => {
  if (props.categories.length <= itemsPerView.value) return
  
  isTransitioning.value = true
  currentIndex.value += 1
  
  // Check if we've reached the cloned items at the end
  setTimeout(() => {
    if (currentIndex.value >= totalOriginalItems.value + itemsPerView.value) {
      isTransitioning.value = false
      currentIndex.value = itemsPerView.value // Jump to first real item
    }
  }, props.animationDuration)
}

const prevSlide = () => {
  if (props.categories.length <= itemsPerView.value) return
  
  isTransitioning.value = true
  currentIndex.value -= 1
  
  // Check if we've reached the cloned items at the beginning
  setTimeout(() => {
    if (currentIndex.value < itemsPerView.value) {
      isTransitioning.value = false
      currentIndex.value = totalOriginalItems.value // Jump to last real item
    }
  }, props.animationDuration)
}

const goToSlide = (dotIndex) => {
  if (props.categories.length <= itemsPerView.value) return
  
  isTransitioning.value = true
  currentIndex.value = itemsPerView.value + (dotIndex * itemsPerView.value)
}

const startAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
  }
  
  if (props.categories.length > itemsPerView.value) {
    autoPlayTimer.value = setInterval(() => {
      if (!isPaused.value) {
        nextSlide()
      }
    }, props.autoPlayInterval)
  }
}

const stopAutoPlay = () => {
  if (autoPlayTimer.value) {
    clearInterval(autoPlayTimer.value)
    autoPlayTimer.value = null
  }
}

const pauseAutoPlay = () => {
  isPaused.value = true
}

const resumeAutoPlay = () => {
  isPaused.value = false
}

const getCategoryUrl = (category) => {
  let url = '/collections/'
  if (category.parent_id && category.parent) {
    url += category.parent.slug + '/' + category.slug
  } else {
    url += category.slug
  }
  return url
}

const truncateText = (text, limit) => {
  if (!text || text.length <= limit) return text
  return text.substring(0, limit) + '...'
}

// Lifecycle hooks
onMounted(() => {
  updateItemsPerView()
  resetToStart()
  startAutoPlay()
  
  window.addEventListener('resize', updateItemsPerView)
})

onUnmounted(() => {
  stopAutoPlay()
  window.removeEventListener('resize', updateItemsPerView)
})

// Watch for changes in categories to restart autoplay
watch(() => props.categories, () => {
  resetToStart()
  startAutoPlay()
}, { deep: true })

// Watch for itemsPerView changes to restart autoplay
watch(itemsPerView, () => {
  startAutoPlay()
})
</script>

<style scoped>
.categories-carousel-wrapper {
  position: relative;
  overflow: hidden;
}

.categories-carousel {
  display: flex;
  width: 100%;
  will-change: transform;
}

.carousel-item {
  flex: 0 0 100%;
  max-width: 100%;
  padding: 0 0.75rem;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .carousel-item {
    flex: 0 0 50%;
    max-width: 50%;
  }
}

@media (min-width: 992px) {
  .carousel-item {
    flex: 0 0 33.333%;
    max-width: 33.333%;
  }
}

.carousel-item .featured-links__item {
  display: block;
  height: 100%;
  text-decoration: none;
  transition: transform 0.3s ease;
}

.carousel-item .featured-links__item:hover {
  transform: translateY(-5px);
}

.carousel-navigation {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
}

.carousel-nav {
  background: var(--config-color-primary, #007bff);
  border: none;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: all 0.3s ease;
  flex-shrink: 0;
}

.carousel-nav:hover:not(:disabled) {
  background: var(--config-color-primary-50, #0056b3);
  transform: scale(1.05);
}

.carousel-nav:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.carousel-nav svg {
  width: 24px;
  height: 24px;
}

.carousel-dots {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.carousel-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: rgba(0, 0, 0, 0.2);
  cursor: pointer;
  transition: all 0.3s ease;
}

.carousel-dot.active {
  background: var(--config-color-primary, #007bff);
  transform: scale(1.2);
}

.carousel-dot:hover {
  background: var(--config-color-primary-50, #0056b3);
  transform: scale(1.1);
}

/* Mobile adjustments */
@media (max-width: 767px) {
  .carousel-navigation {
    gap: 0.5rem;
  }
  
  .carousel-nav {
    width: 40px;
    height: 40px;
  }
  
  .carousel-nav svg {
    width: 20px;
    height: 20px;
  }
  
  .carousel-dot {
    width: 10px;
    height: 10px;
  }
}

/* Animation improvements */
.carousel-item .featured-links__item img {
  transition: transform 0.5s ease, box-shadow 0.3s ease;
}

.carousel-item .featured-links__item:hover img {
  transform: scale(1.02);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
}

/* Accessibility improvements */
@media (prefers-reduced-motion: reduce) {
  .categories-carousel,
  .carousel-item .featured-links__item,
  .carousel-nav,
  .carousel-dot {
    transition: none;
  }
  
  .carousel-item .featured-links__item:hover {
    transform: none;
  }
}

/* Focus styles for accessibility */
.carousel-nav:focus-visible,
.carousel-dot:focus-visible {
  outline: 2px solid var(--config-color-primary, #007bff);
  outline-offset: 2px;
}
</style>
